import type { Building, GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { Doc } from "building-types/doc";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { RWTable, Table } from "common/data/table";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { partialDocToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { createDatumAbstractionComponentInstances } from "../generationTools/components/componentDatumAbstractions";
import { buildComponentTransforms } from "../utils/componentTransforms";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

interface SelectComponentRoomMappingsInput {
  readonly doc: Pick<
    Doc,
    "componentMembers" | "componentInstances" | "datums" | "optionSets" | "instanceOptionSets" | "rooms" | "blocks"
  >;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly enabledFeatures: GenerateBuildingFeatureSet;
  readonly optionChoices: OptionChoices;
}

function selectComponentRoomMappings({
  doc,
  datumAbstractions,
  enabledFeatures,
  optionChoices,
}: SelectComponentRoomMappingsInput) {
  const { componentMembers, componentInstances, datums, rooms, blocks } = doc;
  const definitions = partialDocToOptionDefinitions(doc);
  const componentInstanceOptionChoices = resolveOptionsInFormula(
    { componentInstances, rooms, blocks },
    definitions,
    optionChoices,
  );

  return {
    componentMembers,
    componentInstances,
    datums,
    rooms,
    blocks,
    datumAbstractions,
    enabledFeatures,
    componentInstanceOptionChoices,
    definitions,
    optionChoices,
  };
}

const compare = makeGenerationStageComparator<typeof selectComponentRoomMappings>({
  componentInstanceOptionChoices: "deep",
  definitions: "shallow",
});

function generateComponentRoomMappings({
  componentMembers,
  componentInstances,
  datums,
  datumAbstractions,
  enabledFeatures,
  componentInstanceOptionChoices,
  definitions,
  optionChoices,
  rooms,
  blocks,
}: ReturnType<typeof selectComponentRoomMappings>): Pick<
  Building,
  "idToParents" | "componentToTransforms" | "datumAbstractions"
> {
  // Only generate mappings when modelComponents feature is enabled.
  if (!enabledFeatures.modelComponents) {
    return {
      idToParents: {},
      componentToTransforms: {},
      datumAbstractions,
    };
  }

  const evaluatedComponentInstances = evalFormulas(componentInstances, (id) => componentInstanceOptionChoices[id]);

  const updatedDatumAbstractions: RWTable<DatumAbstraction> = {
    ...datumAbstractions,
  };

  // build component transforms
  const { idToParents, componentToTransforms } = buildComponentTransforms({
    rooms,
    blocks,
    componentMembers,
    evaluatedComponentInstances,
    componentInstances,
    datumAbstractions,
    definitions,
    optionChoices,
  });

  // generate extra datum component abstractions
  Object.values(datums).reduce((acc, datum) => {
    createDatumAbstractionComponentInstances(datum.id, datum, componentToTransforms, acc);
    return acc;
  }, updatedDatumAbstractions);

  return {
    idToParents,
    componentToTransforms,
    datumAbstractions: updatedDatumAbstractions,
  };
}

export default memo(selectComponentRoomMappings, generateComponentRoomMappings, compare);
